import { formatMoney, formatRange } from './quoteEngine';

const escapeHtml = (value) =>
  String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const asParagraphs = (items) =>
  items
    .filter(Boolean)
    .map((item) => '<p>' + escapeHtml(item) + '</p>')
    .join('');

export function buildQuoteDocument(
  quote,
  { dateText, perPersonBudget, logoUrl }
) {
  const preparedDate = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date());

  const eventDate = String(dateText || '').trim() || 'Not provided';

  const rows = quote.lines
    .map((line) => {
      const amount = line.includedElsewhere
        ? 'Not added (food already included)'
        : line.startingAt
          ? 'Starting at ' + formatRange(line.low, line.high)
          : formatRange(line.low, line.high);
      const detailText = String(line.detail || '')
        .replace(/\s*[·|,]\s*Teamward launch price\b/gi, '')
        .trim();
      const confidenceText = String(line.confidenceLabel || '')
        .replace(/^Teamward launch price$/i, '')
        .trim();
      const detail = [
        detailText,
        confidenceText,
      ].filter(Boolean).join(' · ');

      return (
        '<tr><td><strong>' + escapeHtml(line.label) + '</strong>' +
        (detail ? '<span class="detail">' + escapeHtml(detail) + '</span>' : '') +
        '</td><td class="amount">' + escapeHtml(amount) + '</td></tr>'
      );
    })
    .join('');

  const notes = Array.from(new Set([
    ...(quote.warnings || []),
    ...quote.lines.map((line) => line.note).filter(Boolean),
  ]));

  const details = [
    ['Location', 'Denver, CO'],
    ['Group size', quote.input.headcount + (quote.input.headcount === 1 ? ' person' : ' people')],
    ['Event date', eventDate],
  ];

  const metadata = details
    .map((item) =>
      '<div class="meta-item"><span>' + escapeHtml(item[0]) +
      '</span><strong>' + escapeHtml(item[1]) + '</strong></div>'
    )
    .join('');

  const notesHtml = asParagraphs(notes);
  const hasUnknown = quote.quotedSeparately && quote.quotedSeparately.length > 0;
  const estimateNote = [
    hasUnknown
      ? 'The estimate total includes priced items only. Items marked “Quoted separately” are not included.'
      : '',
    quote.hasEstimates
      ? 'Some amounts are planning estimates or allowances and will be confirmed with providers.'
      : '',
    quote.input.weekend
      ? 'Weekend or peak pricing has been applied where a weekend rate is available.'
      : '',
  ].filter(Boolean);

  const budgetTarget = perPersonBudget
    ? '<p class="target">Budget target (separate from estimated cost): ' + escapeHtml(formatMoney(perPersonBudget)) + ' per person</p>'
    : '';

  const html = [
    '<!doctype html><html lang="en"><head><meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width,initial-scale=1">',
    '<title>Teamward team experience estimate</title>',
    '<style>',
    '*,*::before,*::after{box-sizing:border-box}',
    'body{margin:0;background:#f1f3f6;color:#19243a;font-family:Arial,Helvetica,sans-serif;-webkit-font-smoothing:antialiased}',
    '.toolbar{display:flex;justify-content:flex-end;gap:10px;max-width:850px;margin:24px auto 12px;padding:0 24px}',
    '.toolbar button{border:1px solid #102b55;border-radius:999px;padding:10px 17px;background:#fff;color:#102b55;font-size:14px;font-weight:700;cursor:pointer}',
    '.toolbar button.primary{background:#102b55;color:#fff}',
    '.sheet{max-width:850px;min-height:1050px;margin:0 auto 32px;padding:64px 72px;background:#fff;box-shadow:0 12px 40px rgba(16,43,85,.09)}',
    '.top{display:flex;align-items:center;justify-content:space-between;gap:24px;padding-bottom:18px;border-bottom:2px solid #102b55;position:relative}',
    '.top:after{content:"";position:absolute;bottom:-2px;left:0;width:76px;height:3px;background:#43ae65}',
    '.logo-mark{display:block;width:160px;height:104px;overflow:visible}',
    '.prepared{text-align:right;color:#697487;font-size:13px;line-height:1.5}.prepared strong{display:block;color:#102b55;font-size:14px}',
    '.eyebrow{margin:28px 0 8px;color:#315f9d;text-transform:uppercase;letter-spacing:.14em;font-size:11px;font-weight:700}',
    'h1{font-size:32px;line-height:1.15;letter-spacing:-.035em;margin:0 0 24px;color:#102b55}',
    '.meta{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;padding:16px 18px;margin-bottom:26px;background:#f6f8fb;border-radius:8px}',
    '.meta-item span{display:block;margin-bottom:5px;color:#748095;text-transform:uppercase;letter-spacing:.08em;font-size:9px;font-weight:700}',
    '.meta-item strong{font-size:13px;color:#203657}',
    'table{width:100%;border-collapse:collapse}thead th{padding:0 0 10px;color:#7b8492;text-align:left;text-transform:uppercase;letter-spacing:.1em;font-size:10px;border-bottom:1px solid #dce2e9}thead th:last-child{text-align:right}',
    'tbody td{padding:14px 0;border-bottom:1px solid #e6eaf0;vertical-align:top;font-size:14px}tbody td:first-child{padding-right:22px}tbody tr:last-child td{border-bottom:0}',
    'tbody strong{display:block;color:#172a4d;font-size:14px;line-height:1.35}.detail{display:block;margin-top:4px;color:#7a8391;font-size:11px;line-height:1.45}.amount{text-align:right;white-space:nowrap;color:#233a5e;font-size:14px;font-weight:700}',
    '.total{margin-top:18px;padding:21px 24px 18px 22px;border-left:4px solid #43ae65;background:#eef3f8;display:flex;justify-content:space-between;align-items:center;gap:20px;border-radius:0 8px 8px 0}',
    '.total-label{font-size:15px;font-weight:700;color:#102b55}.total-range{display:block;margin-top:4px;color:#102b55;font-size:25px;font-weight:800;letter-spacing:-.03em}.per-person{text-align:right;color:#56657a;font-size:12px;line-height:1.5}.per-person strong{display:block;color:#102b55;font-size:15px}',
    '.target{margin:10px 0 0;text-align:right;color:#56657a;font-size:12px}',
    '.notes{margin-top:31px}.notes h2{margin:0 0 10px;color:#102b55;font-size:15px}.notes p{margin:0 0 7px;color:#5e697b;font-size:11px;line-height:1.55}',
    '.closing{margin:27px 0 0;color:#33445f;font-size:13px;font-weight:600}',
    '.footer{margin-top:48px;padding-top:13px;border-top:1px solid #e0e5eb;color:#7b8492;font-size:10px;letter-spacing:.02em}',
    '@media(max-width:600px){.toolbar{margin:12px auto;padding:0 14px}.sheet{min-height:0;margin:0;padding:30px 22px 38px;box-shadow:none}.logo-mark{width:132px;height:86px}.top{gap:12px;padding-bottom:16px}.prepared{font-size:11px}h1{font-size:27px;margin-bottom:18px}.eyebrow{margin-top:27px;font-size:10px}.meta{gap:8px;padding:13px 11px}.meta-item strong{font-size:11px}.meta-item span{font-size:8px}.meta-item:last-child{grid-column:auto}tbody td{font-size:12px;padding:12px 0}tbody td:first-child{padding-right:12px}.amount{font-size:12px}.detail{font-size:10px}.total{padding:17px 15px;gap:10px}.total-range{font-size:21px}.per-person{font-size:10px}.per-person strong{font-size:13px}.footer{margin-top:35px}}',
    '@media print{@page{size:letter;margin:.65in}body{background:#fff}.toolbar{display:none}.sheet{max-width:none;min-height:0;margin:0;padding:0;box-shadow:none}.top{break-inside:avoid}.meta,tbody tr,.total,.notes{break-inside:avoid}.footer{position:relative;margin-top:36px}}',
    '</style></head><body>',
    '<div class="toolbar"><button class="primary" type="button" onclick="window.print()">Print or save PDF</button></div>',
    '<main class="sheet"><header class="top"><svg class="logo-mark" viewBox="300 185 940 650" role="img" aria-label="Teamward. Better experiences. Stronger teams."><defs><filter id="extract-logo" x="-5%" y="-5%" width="110%" height="110%" color-interpolation-filters="sRGB"><feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 1 1 0 -0.3" result="mask"/><feFlood flood-color="#102b55" result="navy"/><feComposite in="navy" in2="mask" operator="in"/></filter></defs><image href="' + escapeHtml(logoUrl) + '" x="0" y="0" width="1537" height="1023" preserveAspectRatio="none" filter="url(#extract-logo)"/></svg>',
    '<div class="prepared">Prepared<strong>' + escapeHtml(preparedDate) + '</strong></div></header>',
    '<p class="eyebrow">Teamward · Denver, Colorado</p><h1>Your team experience estimate</h1>',
    '<section class="meta" aria-label="Event details">' + metadata + '</section>',
    '<table><thead><tr><th scope="col">Selected experience</th><th scope="col">Estimated cost</th></tr></thead><tbody>' + rows + '</tbody></table>',
    '<section class="total" aria-label="Estimated total"><div><span class="total-label">Estimated total</span><strong class="total-range">' + escapeHtml(formatRange(quote.totals.low, quote.totals.high)) + '</strong></div>',
    '<div class="per-person">Per person<strong>' + escapeHtml(formatRange(quote.totals.perPersonLow, quote.totals.perPersonHigh)) + '</strong></div></section>',
    budgetTarget,
    '<section class="notes"><h2>Planning notes</h2>' + notesHtml + asParagraphs(estimateNote) + '<p>This estimated total reflects the selected items for the group size and event details shown. “Starting at” prices use the listed base rate. Final pricing may change with the event date, provider availability, or event scope; Teamward confirms it before booking.</p></section>',
    '<p class="closing">Ready to explore these options? We’d love to help.</p>',
    '<footer class="footer">teamward.ai&nbsp;&nbsp;|&nbsp;&nbsp;mkuhl@teamward.ai</footer></main>',
    '</body></html>',
  ].join('');

  return html;
}

